import React, { useReducer } from 'react'
import { useMutation } from '@tanstack/react-query'
import { documentUploadReducer } from '../reducers/document-upload'
import { ToastType } from '../components/common/Toast/toast-type'
import {
  DocumentUploadState,
  ProcessedDocument,
  ProcessedFile,
} from '../types/document-upload'
import { formatFileSize } from '../helpers/number'
import { IconChoices } from '../components/common/Icon'
import {
  DocumentActions,
  DocumentUploadActions,
} from '../actions/document-upload'
import { toBase64 } from '../components/common/DropZone/helpers'
import { IDocumentModel } from '../interfaces/models/documents.interfaces'
import {
  buildDocumentRequestBody,
  createDocument,
  deleteDocument,
} from '../api/documents'
import { useToastNotifications } from './useToastNotification'
import { useAppTranslation } from './useAppTranslation'

const isServerId = (id: string | undefined): id is string =>
  !!id && !id.startsWith('blob:') && !id.startsWith('http')

export const useUploadDocument = ({
  maxDocumentUploadLimit = 10,
  societyId,
  section,
}: {
  maxDocumentUploadLimit?: number
  societyId: string
  section: IDocumentModel['section']
}): {
  onDocumentRemove: (id: string) => void
  documentUploading: boolean
  documentUploadDisabled: boolean
  uploadDocument: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>
  uploadFiles: (files: File[]) => void
  documentsState: DocumentUploadState
  documentsDispatch: React.Dispatch<DocumentUploadActions>
} => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { mutate: mutateDeleteDocument } = useMutation({
    mutationFn: deleteDocument,
  })

  const [documentsState, documentsDispatch] = useReducer(
    documentUploadReducer,
    {
      documents: [],
    }
  )

  const hasDocumentLimitExceeded = (documentLength: number): boolean => {
    return (
      documentLength > maxDocumentUploadLimit ||
      documentsState.documents.length + documentLength > maxDocumentUploadLimit
    )
  }

  const documentUploadDisabled =
    documentsState.documents.length >= maxDocumentUploadLimit

  const uploadFiles = (files: File[]): void => {
    if (files.length === 0) {
      return
    }
    if (hasDocumentLimitExceeded(files.length)) {
      setToastNotification(
        ToastType.DANGER,
        translate('chatUpload.flashMessage.uploadDocumentLimitReached')
      )
      return
    }
    files.map(async (file): Promise<void> => {
      const { name } = file
      const uri = URL.createObjectURL(file)
      const _document: ProcessedDocument = {
        uri,
        name,
        size: formatFileSize(file.size),
        icon: IconChoices.DOCUMENT,
      }
      documentsDispatch({
        type: DocumentActions.ADD,
        document: { ..._document, id: uri },
      })

      try {
        const { base64 } = await toBase64(file)
        const body = buildDocumentRequestBody({
          data: base64,
          filename: name,
          name,
          societyId,
          section,
          type: (name.split('.').pop() || '') as IDocumentModel['type'],
        })

        const { data } = (await createDocument(body)).data
        if (!data) {
          throw new Error('createDocument returned no data')
        }
        documentsDispatch({
          type: DocumentActions.SET_UPLOADED,
          id: data._id as string,
          document: _document,
        })
      } catch {
        setToastNotification(
          ToastType.DANGER,
          translate('flashMessage.somethingWentWrongError')
        )
        documentsDispatch({ type: DocumentActions.REMOVE, id: uri })
      }
    })
  }

  const uploadDocument = async (
    e: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const documents = e.target.files
    if (documents) {
      uploadFiles(Array.from(documents))
    }
    e.target.value = ''
  }

  const checkDocumentUploading = (documents: ProcessedFile[]): boolean => {
    return documents.map((document) => document.uploading).includes(true)
  }

  const onDocumentRemove = (id: string): void => {
    if (isServerId(id)) {
      mutateDeleteDocument(id)
    }
    documentsDispatch({
      type: DocumentActions.REMOVE,
      id,
    })
  }
  const documentUploading = checkDocumentUploading(documentsState.documents)

  return {
    onDocumentRemove,
    documentUploading,
    documentUploadDisabled,
    uploadDocument,
    uploadFiles,
    documentsState,
    documentsDispatch,
  }
}
